// External Imports
import { useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { constructDateObject, isoDateToString } from "../../util/format-date";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import SignaturePad from "../UI/SignaturePad";
import LoadingSpinner from "../UI/LoadingSpinner";
import { uploadBase64String } from "../../util/upload-base64";
import { requestAdminAPI } from "../../api/posh-api";

/** ========================================================================
 * * ForceAgreement Component
 * This component renders a form that allows admin to force booking agreement.
 * ========================================================================== */

export default function ForceAgreement({ open, setOpen, booking, onComplete }) {
  // Initialize States
  const [loading, setLoading] = useState(false);

  /* ====================== FORCE AGREEMENT HANDLER ====================== */
  const forceAgreementHandler = async (signature) => {
    try {
      setLoading(true);
      const url =
        booking.customer?.signature ||
        (await uploadBase64String(
          `bookings/${booking.id}/signature`,
          signature
        ));
      const payload = {
        bookingId: booking.id,
        startDate: document.getElementById("startDate").value,
        startTime: document.getElementById("startTime").value,
        endTime: document.getElementById("endTime").value,
        endDate: document.getElementById("endDate").value,
        pickUpLocation: document.getElementById("pickUpLocation").value,
        dropOffLocation: document.getElementById("dropOffLocation").value,
        dateCreated: constructDateObject(
          document.getElementById("dateCreated").value,
          "00:00:00"
        ),
        password: document.getElementById("password").value,
        signature: url,
      };

      const res = await requestAdminAPI("force-agreement", payload);
      window.open(res, "_blank");
      onComplete && onComplete();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err.message);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[45vw] gap-6">
        <Heading
          title="Force Agreement"
          isBackArrow={false}
          text="This will generated a rental agreement with the details below. An admin password is required to use this feature. Please contact an admin to get the password."
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>

        <div className="info">
          <DetailPair
            label="Start Date"
            edit={true}
            inputOptions={{
              type: "date",
              required: true,
              defaultValue: isoDateToString(booking.startDate, "yyyy-MM-dd"),
              id: "startDate",
            }}
          />
          <DetailPair
            label="Start Time"
            edit={true}
            inputOptions={{
              type: "time",
              required: true,
              defaultValue: isoDateToString(booking.startDate, "HH:mm"),
              id: "startTime",
            }}
          />
          <DetailPair
            label="End Date"
            edit={true}
            inputOptions={{
              type: "date",
              required: true,
              defaultValue: isoDateToString(booking.startDate, "yyyy-MM-dd"),
              id: "endDate",
            }}
          />
          <DetailPair
            label="End Time"
            edit={true}
            inputOptions={{
              type: "time",
              required: true,
              defaultValue: isoDateToString(booking.startDate, "HH:mm"),
              id: "endTime",
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              label="Pick Up Location"
              edit={true}
              inputOptions={{
                type: "text",
                required: true,
                defaultValue: booking.pickUpLocation,
                id: "pickUpLocation",
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              label="Drop Off Location"
              edit={true}
              inputOptions={{
                type: "text",
                required: true,
                defaultValue: booking.dropOffLocation,
                id: "dropOffLocation",
              }}
            />
          </div>
          <DetailPair
            label="Date Created"
            edit={true}
            inputOptions={{
              type: "date",
              required: true,
              defaultValue: isoDateToString(booking.dateCreated, "yyyy-MM-dd"),
              id: "dateCreated",
            }}
          />
          <DetailPair
            label="Admin Password"
            edit={true}
            inputOptions={{
              type: "password",
              required: true,
              id: "password",
            }}
          />
        </div>
        {loading ? (
          <LoadingSpinner />
        ) : booking.customer?.signature ? (
          <div className="grid gap-4 text-sm">
            <img
              src={booking.customer.signature}
              className="px-12 py-3 mx-auto bg-gray-200 border-2 border-yellow-600 rounded-xl dark:bg-stone-600"
            />
            <div className="grid gap-4 lg:grid-cols-2 lg:w-1/2 lg:mx-auto">
              <div className="btn-secondary" onClick={() => setOpen(false)}>
                Cancel
              </div>
              <div
                className="btn-primary"
                onClick={() => forceAgreementHandler()}
              >
                Confirm
              </div>
            </div>
          </div>
        ) : (
          <SignaturePad setSignature={forceAgreementHandler} />
        )}
      </div>
    </Modal>
  );
}
